import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import localTheme from "../theme.css";
import defaultFlowConfig from "../config/flowConfig.json";
import defaultQuestions from "../config/questions.json";
import { FirebasePublicUrl } from "../constants";
import { useTranslation } from "react-i18next";
import { GeoLocationUrl } from "../constants";

export const ConfigContext = createContext();

export const useConfig = () => useContext(ConfigContext);

export const ConfigProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [configData, setConfigData] = useState(defaultFlowConfig);
  const [themeUrl, setThemeUrl] = useState(null); // Initially null, set by external theme URL or fallback
  const [logo, setLogo] = useState("logo.png");

  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [isQuestionsDataLoaded, setIsQuestionsDataLoaded] = useState(false);

  const [customerName, setCustomerName] = useState(
    defaultFlowConfig.partnerName,
  );
  const [userQuestions, setUserQuestions] = useState(defaultQuestions);

  // Helper to fetch JSON data from a URL
  const fetchJSON = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch from ${url}`);
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  // handling Geo Blocking
  const handleGeoBlock = async (configData) => {
    if (configData.allowedRegions?.length > 0) {
      const geoResponse = await fetchJSON(GeoLocationUrl);
      if (
        Object.keys(geoResponse).length > 0 &&
        configData.allowedRegions.includes(configData?.country_name)
      ) {
        return false;
      } else return true;
    } else return false;
  };

  // Initialize configuration data and resources for the customer
  const initializeConfig = useCallback(
    async (customerName, fetchOnlyConfig) => {
      if (isConfigLoaded) {
        return {
          response: true,
          geoStatus: await handleGeoBlock(configData),
        };
      }

      const configUrl = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fsrc%2Fconfig%2FflowConfig.json?alt=media`;
      const dbThemeUrl = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fsrc%2Ftheme.css?alt=media`;
      const logoUrl = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fpublic%2Flogo.png?alt=media`;
      const favIconUrl = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fpublic%2Ffavicon.png?alt=media`;

      // Pre-load default values for function response
      let initializerResponse = { response: false, geoStatus: false };
      try {
        // browser assets
        const response = await fetch(favIconUrl, { method: "GET" });
        const favIcon = response.ok ? favIconUrl : "favicon.png";

        const link = document.querySelector("link[rel~='icon']");
        link.href = favIcon;

        // BrowserName goes here

        const externalConfig = await fetchJSON(configUrl);
        if (externalConfig !== null) {
          const newConfigData = { ...defaultFlowConfig, ...externalConfig };
          setConfigData(newConfigData);

          if (fetchOnlyConfig) {
            setIsConfigLoaded(true);
            initializerResponse.geoStatus = await handleGeoBlock(newConfigData);
            initializerResponse.response = true;
            return initializerResponse;
          }

          setThemeUrl(dbThemeUrl);

          setLogo(logoUrl);
          setCustomerName(customerName);

          // Load all the translations
          const languageFiles = [
            "customHomeInfo",
            "customAiInfo",
            "customQuestions",
          ];

          i18n.changeLanguage(newConfigData.defaultLanguage);
          for (const language of newConfigData.availableLanguages) {
            for (const file of languageFiles) {
              const url = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fsrc%2Flocales%2F${language}%2F${file}.json?alt=media`;
              const translation = await fetchJSON(url);
              if (translation) {
                i18n.addResourceBundle(
                  language,
                  file,
                  translation,
                  false,
                  true,
                );
              }
            }
          }

          initializerResponse.geoStatus = await handleGeoBlock(newConfigData);
          initializerResponse.response = true;
        } else {
          throw new Error("Configuration not found");
        }
      } catch (error) {
        console.warn("Fallback to local config due to error:", error);

        // Load all the local default files
        setCustomerName("techdemo");
        setUserQuestions(defaultQuestions);
        setLogo("logo.png");
        setThemeUrl(null);
        setIsQuestionsDataLoaded(true);
      }

      setIsConfigLoaded(true);

      return initializerResponse;
    },
    [isConfigLoaded, i18n],
  );

  // Apply theme by creating a <link> element, with fallback to local theme if needed
  useEffect(() => {
    if (!themeUrl) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = themeUrl;
    link.id = "dynamic-theme";

    link.onload = () => {
      console.log("Theme loaded successfully from:", themeUrl);
    };

    link.onerror = () => {
      console.warn("Failed to load external theme. Reverting to local theme.");
      link.href = localTheme;
    };

    document.head.appendChild(link);

    return () => {
      const existingLink = document.getElementById("dynamic-theme");
      if (existingLink) {
        existingLink.remove();
      }
    };
  }, [themeUrl]);

  // Fetch questions and answers data for the customer, with fallback to default questions
  const fetchQuestionsData = useCallback(async () => {
    if (isQuestionsDataLoaded) return true;

    const questionUrl = `${FirebasePublicUrl}${customerName}%2Ffrontend%2Fsrc%2Fconfig%2Fquestions.json?alt=media`;

    try {
      const questions = await fetchJSON(questionUrl);

      if (questions !== null) {
        setUserQuestions(questions);
      } else {
        throw new Error("Fallback to default questions due to fetch error");
      }
    } catch (error) {
      // No question are showed in this case
      setUserQuestions([]);
    } finally {
      setIsQuestionsDataLoaded(true);
      return true;
    }
  }, [isQuestionsDataLoaded, customerName]);

  return (
    <ConfigContext.Provider
      value={{
        logo,
        configData,
        isConfigLoaded,
        initializeConfig,
        fetchQuestionsData,
        userQuestions,
        isQuestionsDataLoaded,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
